import type React from 'react';
import type { NavigationMapInfo, RobotPose, WaypointMap } from '../../lib/api/navigation';
import { worldToPx } from '../../lib/navigation-utils';

interface NavigationMapProps {
  mapInfo: NavigationMapInfo;
  mapUrl: string;
  robotPose: RobotPose | null;
  path?: Array<[number, number]>;
  waypoints?: WaypointMap;
  /** compact=true: no waypoint labels, smaller dots — for minimap use */
  compact?: boolean;
  className?: string;
  onWaypointClick?: (name: string, e: React.MouseEvent) => void;
}

/**
 * Renders a navigation map: raster image background + SVG overlay
 * with robot pose, planned path, and waypoints.
 *
 * Layout/scrolling/rotation is the parent's responsibility.
 */
export function NavigationMap({
  mapInfo,
  mapUrl,
  robotPose,
  path = [],
  waypoints = {},
  compact = false,
  className = '',
  onWaypointClick,
}: NavigationMapProps) {
  // Slightly bigger dots in the full nav overlay (compact=false), but
  // keep the label/font ratio close to original — over-large station
  // names overwhelm the map.
  const dotR = mapInfo.width * (compact ? 0.02 : 0.014);
  const fontSize = dotR * 1.3;
  const labelPad = dotR * 0.6;

  const robotPx = robotPose ? worldToPx(mapInfo, robotPose) : null;
  const theta = robotPose?.theta ?? 0;

  return (
    <div className={`relative w-full h-full ${className}`}>
      {mapUrl ? (
        <img
          src={mapUrl}
          alt="Navigation map"
          className="w-full h-full object-contain pointer-events-none dark:invert"
          style={{ objectPosition: 'top center' }}
          draggable={false}
        />
      ) : null}

      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        viewBox={`0 0 ${mapInfo.width} ${mapInfo.height}`}
        preserveAspectRatio="xMidYMin meet"
      >
        {/* Path */}
        {path.length > 1 && (
          <>
            <polyline
              className="dark:opacity-0"
              fill="none"
              stroke="rgba(0,0,0,0.12)"
              strokeWidth={dotR * 0.9}
              strokeLinejoin="round"
              strokeLinecap="round"
              points={path.map(([x, y]) => {
                const p = worldToPx(mapInfo, { x, y });
                return `${p.x},${p.y}`;
              }).join(' ')}
            />
            <polyline
              fill="none"
              stroke="#3b82f6"
              strokeWidth={dotR * 0.55}
              strokeLinejoin="round"
              strokeLinecap="round"
              strokeDasharray={`${dotR * 2} ${dotR}`}
              points={path.map(([x, y]) => {
                const p = worldToPx(mapInfo, { x, y });
                return `${p.x},${p.y}`;
              }).join(' ')}
            />
          </>
        )}

        {/* Waypoints */}
        {!compact && Object.entries(waypoints).map(([name, wp]) => {
          const p = worldToPx(mapInfo, wp);
          const color = wp.dockable ? '#22c55e' : '#3b82f6';
          const pillH = fontSize + labelPad * 2;
          const pillBottomY = p.y - dotR - dotR * 0.3;
          const pillCenterY = pillBottomY - pillH / 2;
          const textW = name.length * fontSize * 0.62 + labelPad * 2;
          return (
            <g
              key={name}
              style={onWaypointClick ? { pointerEvents: 'all', cursor: 'pointer' } : undefined}
              onClick={onWaypointClick ? (e) => { e.stopPropagation(); onWaypointClick(name, e); } : undefined}
              onMouseDown={onWaypointClick ? (e) => e.stopPropagation() : undefined}
            >
              <line x1={p.x} y1={p.y - dotR} x2={p.x} y2={pillBottomY} stroke={color} strokeWidth={dotR * 0.18} opacity={0.5} />
              <rect x={p.x - textW / 2} y={pillCenterY - pillH / 2} width={textW} height={pillH} rx={pillH / 2} fill={color} opacity={0.72} />
              <text x={p.x} y={pillCenterY} textAnchor="middle" dominantBaseline="central" fontSize={fontSize} fill="white" fontWeight="700" fontFamily="system-ui, sans-serif">
                {name}
              </text>
              <circle cx={p.x} cy={p.y} r={dotR} fill={color} stroke="white" strokeWidth={dotR * 0.3} />
            </g>
          );
        })}

        {/* Compact waypoints: dots only */}
        {compact && Object.entries(waypoints).map(([name, wp]) => {
          const p = worldToPx(mapInfo, wp);
          const color = wp.dockable ? '#22c55e' : '#3b82f6';
          return (
            <circle key={name} cx={p.x} cy={p.y} r={dotR * 0.7} fill={color} stroke="white" strokeWidth={dotR * 0.25} />
          );
        })}

        {/* Robot pose */}
        {robotPx && (() => {
          const dir = { x: Math.cos(theta), y: -Math.sin(theta) };
          const perp = { x: -dir.y, y: dir.x };
          const arrowLen = dotR * 2.2;
          const halfBase = dotR * 0.45;
          const tipX = robotPx.x + dir.x * arrowLen;
          const tipY = robotPx.y + dir.y * arrowLen;
          const baseX = robotPx.x + dir.x * dotR * 0.6;
          const baseY = robotPx.y + dir.y * dotR * 0.6;
          const pts = [
            `${tipX},${tipY}`,
            `${baseX + perp.x * halfBase},${baseY + perp.y * halfBase}`,
            `${baseX - perp.x * halfBase},${baseY - perp.y * halfBase}`,
          ].join(' ');
          return (
            <g>
              <circle cx={robotPx.x} cy={robotPx.y} r={dotR * 1.7} fill="#ef4444" opacity={0.18} />
              <circle cx={robotPx.x} cy={robotPx.y} r={dotR * 1.1} fill="#ef4444" stroke="white" strokeWidth={dotR * 0.3} />
              <polygon points={pts} fill="#ef4444" stroke="white" strokeWidth={dotR * 0.15} opacity={0.95} />
            </g>
          );
        })()}
      </svg>
    </div>
  );
}
